// src/components/learningDashboard/components/contentHelpers/contentFetch.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { LEGACY_COLLECTION_UPDATED_EVENT } from "../../../../shared/legacyData/legacyCallable";
import { affectsLegacyProgress } from "../../utils/legacyProgressInvalidation";
import { currentProgressRead } from "../../utils/currentProgressRead";

const contentRequests = new Map();
const summaryRequests = new Map();
const requestIdentity = () => {
  try { return getAuth().currentUser?.uid || "anonymous"; }
  catch { return "anonymous"; }
};
if (typeof window !== "undefined") {
  const invalidate = (event) => {
    if (!affectsLegacyProgress(event)) return;
    contentRequests.clear();
    summaryRequests.clear();
  };
  [LEGACY_COLLECTION_UPDATED_EVENT, "learner-quiz-completed", "learner-assessment-reviewed"]
    .forEach((event) => window.addEventListener(event, invalidate));
}

export const makeId = (str = "") =>
  String(str)
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "");

export const buildUnitId = (unitName, programName, company = "TPC_Group") => {
  return `${makeId(unitName)}|||${makeId(programName)}|||${makeId(company)}`;
};

/** Strip "Unit 1: " style prefixes so IDs match programme source docs. */
export const stripUnitOrdinalPrefix = (unitName = "") =>
  String(unitName || "")
    .trim()
    .replace(/^Unit\s+\d+:\s*/i, "")
    .trim();

/** Programme name segment for unitId (matches CourseContentViewer / CourseSidebar). */
export const resolveProgramNameForUnitId = (
  programId = "",
  programData = null,
  courseTitle = "",
) => {
  const fromStructure = String(
    programData?.name || programData?.programName || courseTitle || "",
  ).trim();
  if (fromStructure) return fromStructure;

  const pid = String(programId || "").trim();
  if (!pid) return "";
  const withoutBrand = pid.replace(/-TPC_Group$/i, "");
  if (withoutBrand && withoutBrand !== pid) {
    return withoutBrand.replace(/-/g, " ");
  }
  return pid.split("-")[0];
};

/** Unit id candidates for cloud fetch (tolerant name + legacy programme slug). */
export const buildUnitIdCandidates = (
  unitName,
  programId,
  programData = null,
  courseTitle = "",
  unitRecord = null,
) => {
  const programName = resolveProgramNameForUnitId(programId, programData, courseTitle);
  const pid = String(programId || "").trim();
  const legacyProgramName = pid.split("-")[0];
  const withoutBrand = pid.replace(/-TPC_Group$/i, "");
  const names = [];
  const pushName = (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    if (!names.includes(text)) names.push(text);
    const stripped = stripUnitOrdinalPrefix(text);
    if (stripped && !names.includes(stripped)) names.push(stripped);
  };
  pushName(unitName);
  pushName(unitRecord?.name);
  pushName(unitRecord?.title);

  const ids = [];
  const pushId = (value) => {
    const text = String(value || "").trim();
    if (text && !ids.includes(text)) ids.push(text);
  };
  pushId(unitRecord?.unitId);
  const programNameVariants = [
    legacyProgramName,
    pid,
    programName,
    withoutBrand.replace(/-/g, " "),
    withoutBrand,
    withoutBrand.replace(/-/g, "_"),
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const uniqueProgramNames = [...new Set(programNameVariants)];
  names.forEach((name) => {
    uniqueProgramNames.forEach((progVariant) => {
      pushId(buildUnitId(name, progVariant, "TPC_Group"));
    });
  });
  return ids.slice(0, 8);
};

export const buildOutcomeId = (outcomeName, programName, company = "TPC_Group") => {
  return `${makeId(outcomeName)}|||${makeId(programName)}|||${makeId(company)}`;
};

/** Global serial queue — parallel fetchAIContentBuilderData calls cause 503 on cloud functions. */
let aiContentBuilderFetchChain = Promise.resolve();
let lastAiContentBuilderFetchAt = 0;
const AI_CONTENT_BUILDER_MIN_GAP_MS = 200;

export const fetchAIContentBuilderData = async (
  programId,
  unitId,
  company,
  collectionBrand
) => {
  const requestKey = JSON.stringify([requestIdentity(), programId, unitId, company, collectionBrand]);
  if (contentRequests.has(requestKey)) return contentRequests.get(requestKey);
  const execute = async () => {
    const elapsed = Date.now() - lastAiContentBuilderFetchAt;
    if (elapsed < AI_CONTENT_BUILDER_MIN_GAP_MS) {
      await new Promise((resolve) =>
        setTimeout(resolve, AI_CONTENT_BUILDER_MIN_GAP_MS - elapsed),
      );
    }
    lastAiContentBuilderFetchAt = Date.now();

    const functions = getFunctions();
    const callable = httpsCallable(functions, "fetchAIContentBuilderData");
    const payload = { programId, unitId, company, collectionBrand };
    console.log("[LearnerContent] fetchAIContentBuilderData:", payload);
    try {
      const res = await callable(payload);
      return res.data; // { success, data }
    } catch (err) {
      console.error("[LearnerContent] fetchAIContentBuilderData error:", err);
      return { success: false, message: err.message };
    }
  };

  const enqueue = () => {
    const queued = aiContentBuilderFetchChain.then(execute, execute);
    aiContentBuilderFetchChain = queued.catch(() => {});
    return queued;
  };
  const pending = currentProgressRead(() => {
    const newer = contentRequests.get(requestKey);
    return newer && newer !== pending ? newer : enqueue();
  }, requestIdentity).finally(() => {
    if (contentRequests.get(requestKey) === pending) contentRequests.delete(requestKey);
  });
  contentRequests.set(requestKey, pending);
  return pending;
};

export const fetchSectionContent = async (
  outcomeId,
  sectionId,
  complexity = "base"
) => {
  const functions = getFunctions();
  const callable = httpsCallable(functions, "fetchSectionContent");
  const payload = { outcomeId, sectionId, complexity };
  console.log("[LearnerContent] fetchSectionContent:", payload);
  try {
    const res = await callable(payload);
    return res.data; // { success, content }
  } catch (err) {
    console.error("[LearnerContent] fetchSectionContent error:", err);
    return { success: false, message: err.message };
  }
};

export const fetchSectionAssessment = async (
  outcomeId,
  sectionId,
  complexity = "base"
) => {
  const functions = getFunctions();
  const callable = httpsCallable(functions, "fetchSectionAssessment");
  const payload = { outcomeId, sectionId, complexity };
  console.log("[LearnerContent] fetchSectionAssessment:", payload);
  try {
    const res = await callable(payload);
    let data = res.data;

    // The CF can sometimes return an array of historical versions when
    // assessments are rewritten. Always prefer the latest version.
    if (Array.isArray(data)) {
      data = data[data.length - 1] || {};
    }
    if (Array.isArray(data?.assessment)) {
      data.assessment = data.assessment[data.assessment.length - 1] || {};
    }

    const inner = data?.data ?? data?.content ?? data?.assessment ?? data;
    return { ...data, data: inner, assessment: inner };
  } catch (err) {
    console.error("[LearnerContent] fetchSectionAssessment error:", err);
    return { success: false, message: err.message };
  }
};

/** Fetch learner's completed assessment summary for a unit (used for green checkmarks) */
export const fetchLearnerAssessmentSummary = async ({
  unitId,
  learnerUid,
  company = "TPC_Group",
  collectionBrand = "TechPerformanceCentre",
}) => {
  const key = JSON.stringify([requestIdentity(), unitId, learnerUid, company, collectionBrand]);
  if (summaryRequests.has(key)) return summaryRequests.get(key);
  const promise = currentProgressRead(
    () => {
      const newer = summaryRequests.get(key);
      return newer && newer !== promise ? newer :
        fetchLearnerAssessmentSummaryFresh({ unitId, learnerUid, company, collectionBrand });
    }, requestIdentity)
    .finally(() => {
      if (summaryRequests.get(key) === promise) summaryRequests.delete(key);
    });
  summaryRequests.set(key, promise);
  return promise;
};

const fetchLearnerAssessmentSummaryFresh = async ({ unitId, learnerUid, company, collectionBrand }) => {
  const functions = getFunctions();
  const callable = httpsCallable(functions, "fetchLearnerAssessmentSummary");
  try {
    const res = await callable({
      unitId,
      learnerUid,
      company,
      collectionBrand,
    });
    if (!res.data?.success) return {};
    return res.data.data || {};
  } catch (err) {
    console.error("[contentFetch] fetchLearnerAssessmentSummary error:", err);
    return {};
  }
};

/** Convert the payload we get back from fetchSectionContent
 *  into the shape the ContentRenderer expects.                       */
export const mapCFDocToContent = (raw, complexity = "base") => {
  if (!raw) return { noContent: true };

  // 🔑  The CF now returns `[ { …doc… } ]` – unwrap it if needed
  const doc = Array.isArray(raw) ? raw[0] : raw;

  // still guard against empty / malformed docs
  if (!doc || !Array.isArray(doc.structure) || doc.structure.length === 0) {
    return { noContent: true };
  }

  // add the complexity field so downstream code can read it
  return { ...doc, complexity };
};
